import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../../hooks/useApi';
import './AdminPromoCodes.css';
import { fmtDate } from '../../../utils/ruDates';

function fmt(n) {
  return Number(n || 0).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

function PromoStats({ stats }) {
  if (!stats) return null;

  if (stats.promo_type === 'bonus_percent') {
    return (
      <div className="promo-stats">
        <div className="promo-stats-kpis">
          <div className="promo-kpi"><span>{stats.total_active}</span>активных компаний</div>
          <div className="promo-kpi"><span>{stats.used_count}</span>всего активаций</div>
          <div className="promo-kpi"><span>{fmt(stats.total_bonus_paid)} ₽</span>выплачено бонусов</div>
        </div>

        {stats.active_companies.length > 0 && (
          <>
            <div className="promo-stats-subtitle">Активные сейчас</div>
            <table className="promo-stats-table">
              <thead><tr><th>Компания</th><th>Активирован</th><th>Истекает</th></tr></thead>
              <tbody>
                {stats.active_companies.map((r, i) => (
                  <tr key={i}>
                    <td>{r.company_name}</td>
                    <td>{r.applied_at ? fmtDate(r.applied_at) : '—'}</td>
                    <td>{r.valid_until ? fmtDate(r.valid_until) : '∞'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {stats.redemptions.length > 0 && (
          <>
            <div className="promo-stats-subtitle">История активаций</div>
            <table className="promo-stats-table">
              <thead><tr><th>Компания</th><th>Бонус выплачен</th><th>Дата</th></tr></thead>
              <tbody>
                {stats.redemptions.map((r, i) => (
                  <tr key={i}>
                    <td>{r.company_name}</td>
                    <td>{fmt(r.bonus_credited)} ₽</td>
                    <td>{fmtDate(r.applied_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {stats.active_companies.length === 0 && stats.redemptions.length === 0 && (
          <em className="promo-stats-empty">Промокод ещё не использовался</em>
        )}
      </div>
    );
  }

  if (stats.promo_type === 'fixed_amount') {
    return (
      <div className="promo-stats">
        <div className="promo-stats-kpis">
          <div className="promo-kpi"><span>{stats.used_count}</span>активаций</div>
          <div className="promo-kpi"><span>{fmt(stats.total_credited)} ₽</span>зачислено всего</div>
        </div>

        {stats.redemptions.length > 0 ? (
          <table className="promo-stats-table">
            <thead><tr><th>Компания</th><th>Email</th><th>Зачислено</th><th>Дата</th></tr></thead>
            <tbody>
              {stats.redemptions.map((r, i) => (
                <tr key={i}>
                  <td>{r.company_name}</td>
                  <td>{r.user_email || '—'}</td>
                  <td>{fmt(r.bonus_credited)} ₽</td>
                  <td>{fmtDate(r.applied_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <em className="promo-stats-empty">Промокод ещё не использовался</em>}
      </div>
    );
  }

  if (stats.promo_type === 'agency') {
    return (
      <div className="promo-stats">
        {stats.holder_email ? (
          <div className="promo-stats-ref">Партнёр: <strong>{stats.holder_email}</strong>{stats.holder_name ? ` · ${stats.holder_name}` : ''}</div>
        ) : (
          <em className="promo-stats-empty">Код ещё не активирован партнёром — он вводит его в «Настройки → Реферальная программа»</em>
        )}
        <div className="promo-stats-kpis">
          <div className="promo-kpi"><span>{stats.l1_count}</span>приглашённых L1</div>
          <div className="promo-kpi"><span>{stats.l2_count}</span>приглашённых L2</div>
          <div className="promo-kpi"><span>{fmt(stats.total_commission)} ₽</span>начислено партнёру</div>
        </div>

        {stats.links.length > 0 && (
          <table className="promo-stats-table">
            <thead><tr><th>Приглашённый</th><th>Пополнил</th><th>Начислено партнёрам</th><th>Дата</th></tr></thead>
            <tbody>
              {stats.links.map((row, i) => (
                <tr key={i}>
                  <td>{row.invitee_name}</td>
                  <td>{fmt(row.total_revenue)} ₽</td>
                  <td>{fmt(row.total_commission)} ₽</td>
                  <td>{fmtDate(row.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  return null;
}

const TYPE_LABELS = {
  bonus_percent: 'Бонус % за пополнение',
  fixed_amount: 'Промокод на сумму',
  agency: 'Персональный партнёрский (рефералка)',
};

const EMPTY_FORM = {
  code: '',
  promo_type: 'bonus_percent',
  bonus_percent: '',
  valid_until: '',
  max_uses_total: 0,
  max_uses_per_user: 1,
  fixed_amount: '',
  agent_l1_rub_percent: '',
  agent_l1_balance_percent: '',
  agent_l2_balance_percent: '',
  agent_welcome_bonus_amount: '',
  agent_first_purchase_bonus_percent: '',
  agent_tariff_discount_percent: '',
  registration_only: false,
};

const EMPTY_BULK = {
  count: 20,
  prefix: '',
  code_length: 6,
  promo_type: 'fixed_amount',
  fixed_amount: '',
  bonus_percent: '',
  valid_until: '',
  max_uses_total: 1,
  max_uses_per_user: 1,
  registration_only: false,
};

export default function AdminPromoCodes() {
  const { request } = useApi();
  const [codes, setCodes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activations, setActivations] = useState(null);
  const [activationsPromo, setActivationsPromo] = useState(null);

  // Поиск и фильтры
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Генератор партии
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState(EMPTY_BULK);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (query) params.set('q', query);
      if (filterType) params.set('promo_type', filterType);
      if (filterStatus) params.set('status', filterStatus);
      const r = await request(`/api/v1/admin/promocodes?${params.toString()}`);
      // Бэкенд отдаёт {items,total}; старый формат — просто массив
      const items = Array.isArray(r) ? r : (r?.items || []);
      setCodes(items);
      setTotal(Array.isArray(r) ? items.length : (r?.total ?? items.length));
    } catch { /* ignore */ }
    setLoading(false);
  }, [request, query, filterType, filterStatus]);

  useEffect(() => { load(); }, [load]);

  // Дебаунс строки поиска
  useEffect(() => {
    const t = setTimeout(() => setQuery(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError(''); setSuccess('');

    const payload = {
      code: form.code.trim().toUpperCase(),
      promo_type: form.promo_type,
    };

    if (form.promo_type === 'bonus_percent') {
      if (!form.bonus_percent) return setError('Укажите размер бонуса %');
      payload.bonus_percent = parseInt(form.bonus_percent, 10);
      payload.max_uses_total = parseInt(form.max_uses_total, 10) || 0;
      payload.max_uses_per_user = parseInt(form.max_uses_per_user, 10) || 1;
      if (form.valid_until) payload.valid_until = form.valid_until;
      payload.registration_only = !!form.registration_only;
    } else if (form.promo_type === 'fixed_amount') {
      if (!form.fixed_amount) return setError('Укажите сумму промокода');
      payload.fixed_amount = parseFloat(form.fixed_amount);
      payload.max_uses_total = parseInt(form.max_uses_total, 10) || 0;
      payload.max_uses_per_user = parseInt(form.max_uses_per_user, 10) || 1;
      if (form.valid_until) payload.valid_until = form.valid_until;
      payload.registration_only = !!form.registration_only;
    } else if (form.promo_type === 'agency') {
      if (!form.agent_l1_rub_percent) return setError('Укажите % L1 на вывод');
      payload.agent_l1_rub_percent = parseFloat(form.agent_l1_rub_percent);
      if (form.agent_l1_balance_percent) payload.agent_l1_balance_percent = parseFloat(form.agent_l1_balance_percent);
      if (form.agent_l2_balance_percent) payload.agent_l2_balance_percent = parseFloat(form.agent_l2_balance_percent);
      if (form.agent_welcome_bonus_amount) payload.agent_welcome_bonus_amount = parseFloat(form.agent_welcome_bonus_amount);
      if (form.agent_first_purchase_bonus_percent) payload.agent_first_purchase_bonus_percent = parseFloat(form.agent_first_purchase_bonus_percent);
      if (form.agent_tariff_discount_percent) payload.agent_tariff_discount_percent = parseFloat(form.agent_tariff_discount_percent);
    }

    try {
      if (editId) {
        await request(`/api/v1/admin/promocodes/${editId}`, { method: 'PATCH', body: JSON.stringify(payload) });
        setSuccess('Промокод обновлён');
      } else {
        await request('/api/v1/admin/promocodes', { method: 'POST', body: JSON.stringify(payload) });
        setSuccess('Промокод создан');
      }
      setForm(EMPTY_FORM);
      setEditId(null);
      load();
    } catch (err) {
      setError(err?.detail || err?.message || 'Ошибка сохранения');
    }
  };

  const handleEdit = code => {
    setEditId(code.id);
    setForm({
      code: code.code,
      promo_type: code.promo_type,
      bonus_percent: code.bonus_percent ?? '',
      valid_until: code.valid_until ? code.valid_until.substring(0, 16) : '',
      max_uses_total: code.max_uses_total ?? 0,
      max_uses_per_user: code.max_uses_per_user ?? 1,
      fixed_amount: code.fixed_amount ?? '',
      agent_l1_rub_percent: code.agent_l1_rub_percent ?? '',
      agent_l1_balance_percent: code.agent_l1_balance_percent ?? '',
      agent_l2_balance_percent: code.agent_l2_balance_percent ?? '',
      agent_welcome_bonus_amount: code.agent_welcome_bonus_amount ?? '',
      agent_first_purchase_bonus_percent: code.agent_first_purchase_bonus_percent ?? '',
      agent_tariff_discount_percent: code.agent_tariff_discount_percent ?? '',
      registration_only: !!code.registration_only,
    });
    setError(''); setSuccess('');
  };

  const handleDelete = async id => {
    if (!window.confirm('Удалить промокод?')) return;
    try {
      await request(`/api/v1/admin/promocodes/${id}`, { method: 'DELETE' });
      load();
    } catch (err) {
      setError(err?.detail || err?.message || 'Ошибка удаления');
    }
  };

  const handleToggleStatus = async code => {
    try {
      await request(`/api/v1/admin/promocodes/${code.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: code.status === 'active' ? 'inactive' : 'active' }),
      });
      load();
    } catch (err) {
      setError(err?.detail || err?.message || 'Ошибка');
    }
  };

  const handleStats = async code => {
    if (activationsPromo === code.id) { setActivations(null); setActivationsPromo(null); return; }
    try {
      const r = await request(`/api/v1/admin/promocodes/${code.id}/stats`);
      setActivations(r);
      setActivationsPromo(code.id);
    } catch { setActivations(null); setActivationsPromo(code.id); }
  };

  const handleBulkChange = e => {
    const { name, value, type, checked } = e.target;
    setBulkForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleBulkSubmit = async e => {
    e.preventDefault();
    setBulkError(''); setBulkResult(null); setCopied(false);

    const count = parseInt(bulkForm.count, 10);
    if (!count || count < 1 || count > 500) return setBulkError('Количество: от 1 до 500');

    const payload = {
      count,
      prefix: bulkForm.prefix.trim().toUpperCase(),
      code_length: parseInt(bulkForm.code_length, 10) || 6,
      promo_type: bulkForm.promo_type,
      max_uses_total: parseInt(bulkForm.max_uses_total, 10) || 0,
      max_uses_per_user: parseInt(bulkForm.max_uses_per_user, 10) || 1,
      registration_only: !!bulkForm.registration_only,
    };
    if (bulkForm.promo_type === 'fixed_amount') {
      if (!bulkForm.fixed_amount) return setBulkError('Укажите сумму промокода');
      payload.fixed_amount = parseFloat(bulkForm.fixed_amount);
    } else {
      if (!bulkForm.bonus_percent) return setBulkError('Укажите размер бонуса %');
      payload.bonus_percent = parseInt(bulkForm.bonus_percent, 10);
    }
    if (bulkForm.valid_until) payload.valid_until = bulkForm.valid_until;

    setBulkBusy(true);
    try {
      const r = await request('/api/v1/admin/promocodes/bulk', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setBulkResult(r);
      load();
    } catch (err) {
      setBulkError(err?.detail || err?.message || 'Ошибка генерации');
    }
    setBulkBusy(false);
  };

  const handleCopyCodes = async () => {
    if (!bulkResult?.codes?.length) return;
    try {
      await navigator.clipboard.writeText(bulkResult.codes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard недоступен */ }
  };

  const handleDownloadCodes = () => {
    if (!bulkResult?.codes?.length) return;
    const blob = new Blob([bulkResult.codes.join('\r\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `promocodes-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCancel = () => {
    setForm(EMPTY_FORM);
    setEditId(null);
    setError(''); setSuccess('');
  };

  return (
    <div className="admin-promocodes">
      <div className="promo-header">
        <h2>Промокоды</h2>
        <button
          type="button"
          className={bulkOpen ? 'btn-secondary' : 'btn-primary'}
          onClick={() => { setBulkOpen(o => !o); setBulkError(''); }}
        >
          {bulkOpen ? 'Скрыть генератор' : '⚡ Сгенерировать партию'}
        </button>
      </div>

      {bulkOpen && (
        <div className="promo-form-card promo-bulk-card">
          <h3>Генератор партии промокодов</h3>
          <form onSubmit={handleBulkSubmit} className="promo-form">
            <div className="promo-form-row">
              <label>Количество</label>
              <input name="count" type="number" min="1" max="500" value={bulkForm.count} onChange={handleBulkChange} />
            </div>

            <div className="promo-form-row">
              <label>Тип</label>
              <select name="promo_type" value={bulkForm.promo_type} onChange={handleBulkChange}>
                <option value="fixed_amount">Промокод на сумму</option>
                <option value="bonus_percent">Бонус % за пополнение</option>
              </select>
            </div>

            {bulkForm.promo_type === 'fixed_amount' ? (
              <div className="promo-form-row">
                <label>Сумма ₽</label>
                <input name="fixed_amount" type="number" min="1" step="0.01" value={bulkForm.fixed_amount} onChange={handleBulkChange} placeholder="5000" />
              </div>
            ) : (
              <div className="promo-form-row">
                <label>Бонус %</label>
                <input name="bonus_percent" type="number" min="1" max="200" value={bulkForm.bonus_percent} onChange={handleBulkChange} placeholder="20" />
              </div>
            )}

            <div className="promo-form-row">
              <label>Префикс кода</label>
              <input name="prefix" value={bulkForm.prefix} onChange={handleBulkChange} placeholder="VK-" style={{ textTransform: 'uppercase' }} />
            </div>

            <div className="promo-form-row">
              <label>Длина случайной части</label>
              <input name="code_length" type="number" min="4" max="16" value={bulkForm.code_length} onChange={handleBulkChange} />
            </div>

            <div className="promo-form-row">
              <label>Срок действия</label>
              <input name="valid_until" type="datetime-local" value={bulkForm.valid_until} onChange={handleBulkChange} />
            </div>

            <div className="promo-form-row">
              <label>Лимит всего (0=∞)</label>
              <input name="max_uses_total" type="number" min="0" value={bulkForm.max_uses_total} onChange={handleBulkChange} />
            </div>

            <div className="promo-form-row">
              <label>Лимит на 1 юзера</label>
              <input name="max_uses_per_user" type="number" min="1" value={bulkForm.max_uses_per_user} onChange={handleBulkChange} />
            </div>

            <div className="promo-form-row promo-form-row--checkbox">
              <label>
                <input name="registration_only" type="checkbox" checked={!!bulkForm.registration_only} onChange={handleBulkChange} />
                {' '}Только при регистрации (для новых компаний)
              </label>
            </div>

            <div className="promo-hint">
              Каждый код — отдельный промокод. По умолчанию один код = одна активация.
            </div>

            {bulkError && <div className="promo-error">{bulkError}</div>}

            <div className="promo-form-actions">
              <button type="submit" className="btn-primary" disabled={bulkBusy}>
                {bulkBusy ? 'Генерация...' : 'Сгенерировать'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => { setBulkForm(EMPTY_BULK); setBulkResult(null); setBulkError(''); }}>
                Сбросить
              </button>
            </div>
          </form>

          {bulkResult && (
            <div className="promo-bulk-result">
              <div className="promo-bulk-result-head">
                <strong>Создано: {bulkResult.count}</strong>
                <div className="promo-bulk-result-actions">
                  <button type="button" className="btn-secondary" onClick={handleCopyCodes}>
                    {copied ? '✓ Скопировано' : 'Копировать списком'}
                  </button>
                  <button type="button" className="btn-secondary" onClick={handleDownloadCodes}>Скачать .txt</button>
                </div>
              </div>
              <textarea className="promo-bulk-codes" readOnly value={bulkResult.codes.join('\n')} rows={Math.min(bulkResult.codes.length, 20)} />
            </div>
          )}
        </div>
      )}

      <div className="promo-form-card">
        <h3>{editId ? 'Редактировать промокод' : 'Новый промокод'}</h3>
        <form onSubmit={handleSubmit} className="promo-form">
          <div className="promo-form-row">
            <label>Код</label>
            <input
              name="code"
              value={form.code}
              onChange={handleChange}
              placeholder="EXAMPLE-2026"
              required
              disabled={!!editId}
              style={{ textTransform: 'uppercase' }}
            />
          </div>

          <div className="promo-form-row">
            <label>Тип</label>
            <select name="promo_type" value={form.promo_type} onChange={handleChange} disabled={!!editId}>
              {Object.entries(TYPE_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>

          {form.promo_type === 'bonus_percent' && (
            <>
              <div className="promo-form-row">
                <label>Бонус %</label>
                <input name="bonus_percent" type="number" min="1" max="200" value={form.bonus_percent} onChange={handleChange} placeholder="20" />
              </div>
              <div className="promo-form-row">
                <label>Срок действия</label>
                <input name="valid_until" type="datetime-local" value={form.valid_until} onChange={handleChange} />
              </div>
              <div className="promo-form-row">
                <label>Лимит всего (0=∞)</label>
                <input name="max_uses_total" type="number" min="0" value={form.max_uses_total} onChange={handleChange} />
              </div>
              <div className="promo-form-row">
                <label>Лимит на 1 юзера</label>
                <input name="max_uses_per_user" type="number" min="1" value={form.max_uses_per_user} onChange={handleChange} />
              </div>
              <div className="promo-form-row promo-form-row--checkbox">
                <label>
                  <input name="registration_only" type="checkbox" checked={!!form.registration_only} onChange={handleChange} />
                  {' '}Только при регистрации (для новых компаний)
                </label>
              </div>
            </>
          )}

          {form.promo_type === 'fixed_amount' && (
            <>
              <div className="promo-form-row">
                <label>Сумма ₽</label>
                <input name="fixed_amount" type="number" min="1" step="0.01" value={form.fixed_amount} onChange={handleChange} placeholder="1000" />
              </div>
              <div className="promo-form-row">
                <label>Срок действия</label>
                <input name="valid_until" type="datetime-local" value={form.valid_until} onChange={handleChange} />
              </div>
              <div className="promo-form-row">
                <label>Лимит всего (0=∞)</label>
                <input name="max_uses_total" type="number" min="0" value={form.max_uses_total} onChange={handleChange} />
              </div>
              <div className="promo-form-row">
                <label>Лимит на 1 юзера</label>
                <input name="max_uses_per_user" type="number" min="1" value={form.max_uses_per_user} onChange={handleChange} />
              </div>
              <div className="promo-form-row promo-form-row--checkbox">
                <label>
                  <input name="registration_only" type="checkbox" checked={!!form.registration_only} onChange={handleChange} />
                  {' '}Только при регистрации (для новых компаний)
                </label>
              </div>
            </>
          )}

          {form.promo_type === 'agency' && (
            <>
              <p className="promo-form-hint">
                Код выдаётся одному партнёру (блогеру): он активирует его в «Настройки → Реферальная программа»,
                второй человек активировать уже не сможет. Приглашённые приходят по ссылке партнёра
                или вводят этот же код при регистрации. Пустое поле — значение из условий по умолчанию
                («Рефералы» в админке).
              </p>
              <div className="promo-form-row">
                <label>% L1 на вывод (руб)</label>
                <input name="agent_l1_rub_percent" type="number" min="0" step="0.01" value={form.agent_l1_rub_percent} onChange={handleChange} placeholder="15" />
              </div>
              <div className="promo-form-row">
                <label>% L1 на баланс</label>
                <input name="agent_l1_balance_percent" type="number" min="0" step="0.01" value={form.agent_l1_balance_percent} onChange={handleChange} placeholder="25" />
              </div>
              <div className="promo-form-row">
                <label>% L2 на баланс</label>
                <input name="agent_l2_balance_percent" type="number" min="0" step="0.01" value={form.agent_l2_balance_percent} onChange={handleChange} placeholder="3" />
              </div>
              <div className="promo-form-row">
                <label>Welcome-бонус приглашённому ₽ (сразу)</label>
                <input name="agent_welcome_bonus_amount" type="number" min="0" step="0.01" value={form.agent_welcome_bonus_amount} onChange={handleChange} placeholder="500" />
              </div>
              <div className="promo-form-row">
                <label>Приглашённому % к первому пополнению</label>
                <input name="agent_first_purchase_bonus_percent" type="number" min="0" step="0.01" value={form.agent_first_purchase_bonus_percent} onChange={handleChange} placeholder="25" />
              </div>
              <div className="promo-form-row">
                <label>% скидка на тариф (пока не применяется)</label>
                <input name="agent_tariff_discount_percent" type="number" min="0" step="0.01" value={form.agent_tariff_discount_percent} onChange={handleChange} placeholder="0" />
              </div>
            </>
          )}

          {error && <div className="promo-error">{error}</div>}
          {success && <div className="promo-success">{success}</div>}

          <div className="promo-form-actions">
            <button type="submit" className="btn-primary">
              {editId ? 'Сохранить' : 'Создать'}
            </button>
            {editId && (
              <button type="button" className="btn-secondary" onClick={handleCancel}>Отмена</button>
            )}
          </div>
        </form>
      </div>

      <div className="promo-search-bar">
        <input
          className="promo-search-input"
          type="search"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          placeholder="Поиск по коду…"
        />
        <select value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">Все типы</option>
          {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Все статусы</option>
          <option value="active">Активные</option>
          <option value="inactive">Отключённые</option>
        </select>
        {(searchInput || filterType || filterStatus) && (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => { setSearchInput(''); setFilterType(''); setFilterStatus(''); }}
          >Сбросить</button>
        )}
        <span className="promo-search-count">Найдено: {total}</span>
      </div>

      {loading ? <p>Загрузка...</p> : (
        <table className="promo-table">
          <thead>
            <tr>
              <th>Код</th>
              <th>Тип</th>
              <th>Параметры</th>
              <th>Лимиты</th>
              <th>Использований</th>
              <th>Кто воспользовался</th>
              <th>Статус</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {codes.map(c => (
              <React.Fragment key={c.id}>
                <tr>
                  <td><strong>{c.code}</strong></td>
                  <td>{TYPE_LABELS[c.promo_type] || c.promo_type}</td>
                  <td>
                    {c.promo_type === 'bonus_percent' && `+${c.bonus_percent}%`}
                    {c.promo_type === 'fixed_amount' && `${c.fixed_amount} ₽`}
                    {c.promo_type === 'agency' && (
                      <span>
                        L1 руб: {c.agent_l1_rub_percent}% / L1 баланс: {c.agent_l1_balance_percent}% / L2: {c.agent_l2_balance_percent}%
                        {c.agent_welcome_bonus_amount ? ` / бонус: ${c.agent_welcome_bonus_amount}₽` : ''}
                      </span>
                    )}
                  </td>
                  <td>
                    {c.promo_type !== 'agency'
                      ? <>{c.max_uses_total ? c.max_uses_total : '∞'} / на юзера: {c.max_uses_per_user}</>
                      : '—'}
                  </td>
                  <td>{c.used_count}</td>
                  <td className="promo-users-cell">
                    {(c.redeemed_by || []).length === 0 ? (
                      <span className="promo-users-empty">—</span>
                    ) : (
                      <>
                        {(c.redeemed_by || []).slice(0, 3).map((r, i) => (
                          <div key={i} className="promo-user-row">
                            <span className="promo-user-name">{r.company_name}</span>
                            {r.user_email && <span className="promo-user-email">{r.user_email}</span>}
                            <span className="promo-user-date">
                              {r.applied_at ? fmtDate(r.applied_at) : ''}
                            </span>
                          </div>
                        ))}
                        {(c.redeemed_by || []).length > 3 && (
                          <button type="button" className="promo-user-more" onClick={() => handleStats(c)}>
                            ещё {(c.redeemed_by || []).length - 3} →
                          </button>
                        )}
                      </>
                    )}
                  </td>
                  <td>
                    <span className={`promo-badge promo-badge--${c.status}`}>
                      {c.status === 'active' ? 'Активен' : 'Отключён'}
                    </span>
                  </td>
                  <td className="promo-actions">
                    <button onClick={() => handleEdit(c)} title="Редактировать">✏️</button>
                    <button onClick={() => handleToggleStatus(c)} title={c.status === 'active' ? 'Отключить' : 'Включить'}>
                      {c.status === 'active' ? '⏸' : '▶'}
                    </button>
                    <button
                      onClick={() => handleStats(c)}
                      title="Статистика"
                      className={activationsPromo === c.id ? 'btn-stats-active' : ''}
                    >📊</button>
                    <button onClick={() => handleDelete(c.id)} title="Удалить" className="btn-danger">🗑</button>
                  </td>
                </tr>
                {activationsPromo === c.id && activations && (
                  <tr>
                    <td colSpan={8} className="promo-stats-panel">
                      <PromoStats stats={activations} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {codes.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', color: '#888' }}>
                {query || filterType || filterStatus ? 'Ничего не найдено' : 'Промокодов нет'}
              </td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
